import { useCallback, useState } from 'react';
import {
  EditableContainer,
  Grid,
  Panel,
  PanelContent,
  Typography,
  TypographyWithTooltip,
  WidgetContainer,
  G600,
  G900,
  S600,
  Select,
  InputAdornment,
  GlobeIcon,
  MenuItem,
  useTranslations,
  useHeroSnackbar,
  TSelectProps,
  PromptDialog,
} from '@uniqkey-frontend/shared-app';
import { MeResponse } from '@uniqkey-backend-partner/api-client';
import { useQueryClient } from 'react-query';
import {
  useGetCurrentUser,
  useUpdateCurrentUser,
  useLanguages,
  REACT_QUERY_CURRENT_USER_KEY,
} from '../../../../hooks/reactQuery';
import { getActualUILanguageCode } from '../../../../helpers/languages';
import EditProfileModal, { IEditProfileModalSubmitResult } from '../EditProfileModal';
import useUserProfileAPI from '../../../../hooks/useUserProfileAPI';
import { logException } from '../../../../services/sentryService';

const SELECT_SX = { width: 215 };
const SELECT_START_ADORNMENT = (
  <InputAdornment position="start">
    <GlobeIcon color={S600} />
  </InputAdornment>
);

const USER_WITH_SUCH_EMAIL_ALREADY_EXISTS_ERROR = 'User_with_such_email_already_exists';

const ProfileModule = () => {
  const { t } = useTranslations();
  const { showError, showSuccess } = useHeroSnackbar();
  const { updateCurrentUser } = useUserProfileAPI();
  const queryClient = useQueryClient();

  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isChangeLanguageModalOpen, setIsChangeLanguageModalOpen] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const handleEditProfileModalOpen = useCallback(() => setIsEditProfileModalOpen(true), []);
  const handleEditProfileModalClose = useCallback(() => setIsEditProfileModalOpen(false), []);
  const handleChangeLanguageModalOpen = useCallback(() => setIsChangeLanguageModalOpen(true), []);
  const handleChangeLanguageModalClose = useCallback(() => setIsChangeLanguageModalOpen(false), []);
  const [isCurrentUserLanguageUpdating, setIsCurrentUserLanguageUpdating] = useState(false);

  const {
    data: currentUser, isError, isLoading: isCurrentUserLoading,
  } = useGetCurrentUser();
  const {
    mutate: mutateCurrentUserProfile,
    isLoading: isCurrentUserProfileUpdating,
  } = useUpdateCurrentUser({
    useOptimisticUpdates: true,
  });

  const { languages, isLoading: areLanguagesLoading } = useLanguages({
    includeOnlyAllowedLanguages: true,
  });

  const handleEditProfile = useCallback((value: IEditProfileModalSubmitResult) => {
    mutateCurrentUserProfile(
      { ...currentUser, ...value },
      {
        onError: (e: any) => {
          let key = 'common.somethingWentWrong';
          if (e?.response?.data?.includes(USER_WITH_SUCH_EMAIL_ALREADY_EXISTS_ERROR)) {
            key = 'profilePage.toast.userWithSuchEmailAlreadyExists';
          }
          showError({ text: t(key) });
          logException(e, {
            message: 'ProfileModule/handleEditProfile exception',
          });
        },
        onSuccess: () => {
          showSuccess({ text: t('profilePage.toast.profileUpdated') });
          handleEditProfileModalClose();
        },
      },
    );
  }, [
    mutateCurrentUserProfile,
    currentUser,
    handleEditProfileModalClose,
    showError,
    showSuccess,
    t,
  ]);

  const handleLanguageSelect = useCallback<NonNullable<TSelectProps['onChange']>>((event) => {
    handleChangeLanguageModalOpen();
    setSelectedLanguage(event.target.value as string);
  }, [handleChangeLanguageModalOpen]);

  const handleLanguageChange = useCallback(async () => {
    try {
      setIsCurrentUserLanguageUpdating(true);
      await updateCurrentUser({ ...currentUser, language: selectedLanguage });
      await queryClient.invalidateQueries(REACT_QUERY_CURRENT_USER_KEY);
      // close the modal right after the current user is updated
      handleChangeLanguageModalClose();
    } catch (e) {
      showError({ text: t('common.somethingWentWrong') });
    } finally {
      setIsCurrentUserLanguageUpdating(false);
    }
  }, [
    currentUser,
    updateCurrentUser,
    selectedLanguage,
    queryClient,
    handleChangeLanguageModalClose,
    showError,
    t,
  ]);

  if (isError) {
    return null;
  }

  const { name, email, language: preLanguage } = currentUser ?? {} as MeResponse;

  const language = getActualUILanguageCode(preLanguage);

  const isCurrentUserUpdating = isCurrentUserProfileUpdating || isCurrentUserLanguageUpdating;

  return (
    <Panel className="height-100-percent">
      <PanelContent p={3} pt={3.5}>
        <Grid container gap={2}>
          <WidgetContainer
            item
            withShadow
            /*
             While the data is loading, we need to have padding here so the skeleton doesn't
             take the full widget size.
             When the data is loaded - we set the proper padding in the EditableContainer below.
            */
            p={isCurrentUserLoading ? 3 : 0}
            xs={12}
            md={6}
            isLoading={isCurrentUserLoading}
          >
            <EditableContainer
              item
              container
              flexDirection="column"
              p={isCurrentUserLoading ? 0 : 3}
              rowGap={3}
              truncate={false}
              onClick={handleEditProfileModalOpen}
              disabled={isCurrentUserUpdating}
            >
              <Grid container flexDirection="column">
                <Typography variant="caption" color={G600} mb={1}>
                  {t('profilePage.name')}
                </Typography>
                <TypographyWithTooltip variant="body3" color={G900}>
                  {name}
                </TypographyWithTooltip>
              </Grid>
              <Grid container flexDirection="column">
                <Typography variant="caption" color={G600} mb={1}>
                  {t('profilePage.email')}
                </Typography>
                <TypographyWithTooltip variant="body3" color={G900}>
                  {email}
                </TypographyWithTooltip>
              </Grid>
            </EditableContainer>
          </WidgetContainer>
          <WidgetContainer
            container
            item
            withShadow
            p={3}
            xs={12}
            md
            isLoading={areLanguagesLoading}
          >
            <Grid container flexDirection="column">
              <Typography variant="caption" color={G600} mb={1}>
                {t('profilePage.changeLanguage')}
              </Typography>
              <Select
                value={language}
                onChange={handleLanguageSelect}
                sx={SELECT_SX}
                startAdornment={SELECT_START_ADORNMENT}
                disabled={isCurrentUserUpdating}
              >
                {
                  languages.map((currLanguage) => (
                    <MenuItem key={currLanguage.code} value={currLanguage.code}>
                      {currLanguage.localized_name}
                    </MenuItem>
                  ))
                }
              </Select>
            </Grid>
          </WidgetContainer>
        </Grid>
      </PanelContent>
      {isEditProfileModalOpen && (
        <EditProfileModal
          isOpen={isEditProfileModalOpen}
          isLoading={isCurrentUserUpdating}
          onSubmit={handleEditProfile}
          onClose={handleEditProfileModalClose}
          currentUser={currentUser!}
        />
      )}
      {isChangeLanguageModalOpen && (
        <PromptDialog
          open={isChangeLanguageModalOpen}
          onClose={handleChangeLanguageModalClose}
          onSubmit={handleLanguageChange}
          title={t('profilePage.changeLanguageModal.title')}
          approvalButtonText={t('common.continue')}
          rejectionButtonText={t('common.cancel')}
          approvalButtonColor="primary"
          isLoading={isCurrentUserLanguageUpdating}
        />
      )}
    </Panel>
  );
};

export default ProfileModule;
